/**
 * Labstep
 *
 * @module components/FilterPost/Report/Metadata/Active/Post
 * @desc Active node component for metadata
 */

import type { FilterPostActiveLabelProps } from 'labstep-web/components/FilterPost/Active/Label/types';
import { FilterPostReportMetadata } from 'labstep-web/components/FilterPost/Report/Metadata';
import { ICONS } from 'labstep-web/constants/icons';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { RemoveLabel } from 'labstep-web/core/Label/Remove';
import {
  MetadataType,
  MetadataTypeValues,
} from 'labstep-web/models/metadata/types';
import type { PostFilterComparisonType } from 'labstep-web/services/postFilter/constants';
import { PostFilterComparison } from 'labstep-web/services/postFilter/constants';
import {
  getPostFilterMetadatasDate,
  getPostFilterMetadatasDateWithName,
  getPostFilterMetadatasDefault,
  getPostFilterMetadatasDefaultWithName,
  getPostFilterMetadatasNumeric,
  getPostFilterMetadatasNumericWithName,
  getPostFilterMetadatasOptions,
  getPostFilterMetadatasOptionsWithName,
} from 'labstep-web/services/postFilter/filters/metadatas';
import { filter } from 'lodash';
import type React from 'react';
import { getComparisonLabelDict } from '../../Form/fields';
import type { FilterMetadataType } from '../../Form/types';

const PATHS = [
  'metadatas',
  'resourceItem.metadatas',
  'resourceItem.resource.metadatas',
];

const FILTERS = PATHS.flatMap((path) => [
  getPostFilterMetadatasDate(path),
  getPostFilterMetadatasDefault(path),
  getPostFilterMetadatasNumeric(path),
  getPostFilterMetadatasOptions(path),
]);

const FILTERS_WITH_NAME = PATHS.flatMap((path) => [
  getPostFilterMetadatasDateWithName(path),
  getPostFilterMetadatasDefaultWithName(path),
  getPostFilterMetadatasNumericWithName(path),
  getPostFilterMetadatasOptionsWithName(path),
]);

export const FilterPostReportMetadataActivePost: React.FC<
  FilterPostActiveLabelProps
> = ({ node, onRemoveClick, index }) => {
  const { addPostFilter } = useParamsContext();
  let filterActive = FILTERS.find((filter) =>
    filter.isNodeActive(node),
  );
  let path = filterActive?.getPath();
  if (!filterActive) {
    filterActive = FILTERS_WITH_NAME.find((filter) =>
      filter.isNodeActive(node, undefined, true),
    );
    path = filterActive?.getPredicates()[1][2] || '';
  }

  if (!filterActive) {
    return null;
  }

  const attributes = filterActive.getAttributeValues(node);
  const protocolValueName = (attributes as any).name?.value;
  if (
    attributes?.value?.comparison === PostFilterComparison.not_null
  ) {
    return null;
  }

  let type: FilterMetadataType;
  let comparison: PostFilterComparisonType;
  let value: string;
  if (attributes.options) {
    type = MetadataType.options;

    comparison = attributes.options.comparison;
    value = attributes.options.value;
  } else if (attributes.date) {
    type = MetadataType.date;
    comparison = attributes.date.comparison;
    value = attributes.date.value;
  } else if (attributes.number) {
    type = MetadataType.numeric;
    comparison = attributes.number.comparison;
    value = attributes.number.value;
  } else {
    type = MetadataType.default;
    comparison = attributes.value?.comparison;
    value = attributes.value?.value;
  }

  const defaultValues = {
    label: attributes.label?.value,
    type: {
      value: type,
      label:
        MetadataTypeValues[type as keyof typeof MetadataTypeValues],
    },
    comparison: {
      value: comparison,
      label: getComparisonLabelDict(type)[comparison],
    },
    value,
  };

  return (
    <FilterPostReportMetadata
      path={filterActive.getPath() || ''}
      addPostFilter={addPostFilter}
      defaultValues={defaultValues}
      viewComponent={({ toggleModal }) => (
        <RemoveLabel
          name={
            protocolValueName
              ? `${protocolValueName}: ${filterActive.getHumanReadableString(node, defaultValues.comparison.label)}`
              : filterActive.getHumanReadableString(
                  node,
                  defaultValues.comparison.label,
                )
          }
          icon={
            path?.includes('resourceItem.metadatas')
              ? ICONS.metadata.secondary
              : ICONS.metadata.primary
          }
          onNameClick={toggleModal}
          onRemoveClick={onRemoveClick}
        />
      )}
      replace={index}
    />
  );
};
